import { useEffect, useState, useCallback, useMemo } from "react";
import { useParams, useNavigate, useLocation } from "react-router-dom";
import {
  FaDesktop,

  FaTrash,
  FaUser,
  FaUserShield,
  FaUserTie,
  FaTools,
  FaHardHat,
  FaChartLine,
  FaCalculator,
  FaWarehouse,
  FaCode,
  FaIndustry,
  FaPencilAlt,
  FaTimes,
  FaEnvelope,
  FaBuilding,
  FaShieldAlt,
  FaToggleOn,
  FaToggleOff,
  FaSave,
  FaUndo,
  FaHistory,
  FaWallet,
} from "react-icons/fa";
import { useLazyGetUserQuery, useUpdateUserMutation, useDeleteUserByIDMutation, useGetTrustedDevicesQuery, useLazyGetRoleTemplatesQuery, useLazySearchFirmsForDropdownQuery } from "../../store";
import useAuth from "../../Hooks/useAuth";
import usePermission from "../../Hooks/usePermission";
import DeviceAssignmentModal from "../../Components/Utils/DeviceAssignmentModal";
import PasswordConfirmModal from "../../Components/Utils/PasswordConfirmModal";
import FormatDateString from "../../Components/Utils/Date/FormatDateString";
import PermissionsTab from "../../Components/User/PermissionsTab";
import UserActivityTab from "../../Components/User/UserActivityTab";
import EndUserWalletTab from "../../Components/User/EndUserWalletTab";
import RoleTemplateDropdown from "../../Components/User/RoleTemplateDropdown";
import CustomDropdown from "../../Components/atoms/CustomDropdown";
import {
  DetailShell,
  DetailHeader,
  DetailTabs,
  DetailSkeleton,
  DetailNotFound,
  InfoCard,
} from "../../Components/shared";
import { getRoleLabel, getRoleBadgeClass } from "../../utils/roleBadge";
import { getFirmTypeStyle } from "../../utils/firmTypeStyles";

// ─── Role helpers ─── use subRole for display when available, fall back to role
const getEffectiveRole = (user) => user?.subRole || user?.role;

const getRoleName = (role) => {
  const roles = {
    admin: "Sistem Yöneticisi", developer: "Geliştirici", management: "Yönetim",
    storage: "Depo", dealer: "Bayi",
    playmaker: "Plasiyer", service: "Servis", accounting: "Muhasebe", depo: "Depo",
    manufacturer: "Üretim", customer: "Müşteri", enduser: "Son Kullanıcı",
    machineworker: "Makine İşçisi",
  };
  return roles[role] || role;
};

const getRoleColor = (role) => {
  const colors = {
    admin: "bg-purple-50 text-purple-700 border-purple-200",
    developer: "bg-sky-50 text-sky-700 border-sky-200",
    management: "bg-blue-50 text-blue-700 border-blue-200",
    storage: "bg-slate-50 text-slate-700 border-slate-200",
    dealer: "bg-violet-50 text-violet-700 border-violet-200",
    playmaker: "bg-emerald-50 text-emerald-700 border-emerald-200",
    service: "bg-indigo-50 text-indigo-700 border-indigo-200",
    accounting: "bg-amber-50 text-amber-700 border-amber-200",
    depo: "bg-sky-50 text-sky-700 border-sky-200",
    manufacturer: "bg-orange-50 text-orange-700 border-orange-200",
    customer: "bg-teal-50 text-teal-700 border-teal-200",
    enduser: "bg-gray-50 text-gray-700 border-gray-200",
    machineworker: "bg-green-50 text-green-700 border-green-200",
  };
  return colors[role] || "bg-gray-50 text-gray-700 border-gray-200";
};

const getRoleIcon = (role) => {
  const icons = {
    admin: FaUserShield, developer: FaCode, management: FaUserTie,
    storage: FaUserShield, dealer: FaUserTie,
    playmaker: FaChartLine, service: FaTools, accounting: FaCalculator, depo: FaWarehouse,
    manufacturer: FaIndustry, customer: FaUser, enduser: FaUser,
    machineworker: FaHardHat,
  };
  return icons[role] || FaUser;
};

const SERVICE_SUB_ROLES = [
  { id: "admin", label: "Yönetici" }, { id: "repair", label: "Onarım" },
  { id: "faultdetection", label: "Arıza Tespiti" }, { id: "packaging", label: "Paketleme" },
  { id: "test", label: "Test" },
];
const getSubRoleName = (subRole) => {
  // Önce servis listesi, sonra genel rol sözlüğü (management → Yönetim vb.);
  // şablon adları sözlükte yoksa olduğu gibi gösterilir.
  return SERVICE_SUB_ROLES.find((r) => r.id === subRole)?.label || getRoleName(subRole) || "-";
};
const WEEK_DAYS = [
  { id: "mon", label: "Pzt" }, { id: "tue", label: "Sal" }, { id: "wed", label: "Çar" },
  { id: "thu", label: "Per" }, { id: "fri", label: "Cum" }, { id: "sat", label: "Cmt" },
  { id: "sun", label: "Paz" },
];

// ─── Editable text field (local state, no API) ───
const EMAIL_DESENI = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

// Kart satırları — InfoCard.Row içinde: etiket solda, değer sağda; düzenleme
// satır içinde açılır (kart yüksekliği satır kadar büyür, düzen kaymaz).
const EditableRow = ({ label, value, onChange, type = "text", placeholder = "-", isDirty, disabled }) => {
  const [editing, setEditing] = useState(false);
  const [tempValue, setTempValue] = useState(value || "");

  useEffect(() => { if (!editing) setTempValue(value || ""); }, [value, editing]);

  // E-posta alanında biçim zorunlu: geçersizken onay düğmesi kapalı kalır ve
  // altında uyarı görünür. Sadece type="email" yetmiyordu — alan bir form
  // içinde olmadığı için tarayıcının kendi doğrulaması hiç tetiklenmiyor.
  const emailAlani = type === "email";
  const bicimHatali = emailAlani && tempValue.trim() !== "" && !EMAIL_DESENI.test(tempValue.trim());
  const onaylanabilir = !bicimHatali && (!emailAlani || tempValue.trim() !== "");

  const handleConfirm = () => { if (!onaylanabilir) return; onChange(tempValue.trim()); setEditing(false); };
  const handleCancel = () => { setTempValue(value || ""); setEditing(false); };
  const handleKeyDown = (e) => { if (e.key === "Enter") handleConfirm(); if (e.key === "Escape") handleCancel(); };

  return (
    <InfoCard.Row label={label} dirty={isDirty && !disabled}>
      {editing ? (
        <div className="flex flex-col items-end gap-1 w-full">
          <div className="flex items-center gap-1.5">
            <input type={type} value={tempValue} onChange={(e) => setTempValue(e.target.value)} onKeyDown={handleKeyDown}
              inputMode={emailAlani ? "email" : undefined}
              autoCapitalize={emailAlani ? "none" : undefined}
              spellCheck={emailAlani ? false : undefined}
              className={`w-44 sm:w-52 text-[13px] text-gray-900 bg-white border rounded-lg px-2.5 py-1 focus:outline-none focus:ring-2 ${
                bicimHatali ? "border-fifth focus:ring-fifth/40" : "border-blue-300 focus:ring-blue-400"
              }`}
              autoFocus placeholder={placeholder} />
            <button onClick={handleConfirm} disabled={!onaylanabilir}
              className="p-1.5 bg-blue-500 text-white rounded-md hover:bg-blue-600 transition-colors shrink-0 disabled:opacity-40 disabled:cursor-not-allowed" title="Onayla">
              <svg className="w-3 h-3" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={3} d="M5 13l4 4L19 7" /></svg>
            </button>
            <button onClick={handleCancel} className="p-1.5 bg-gray-400 text-white rounded-md hover:bg-gray-500 transition-colors shrink-0" title="Vazgeç">
              <FaTimes className="w-3 h-3" />
            </button>
          </div>
          {bicimHatali && (
            <p className="text-[11px] text-fifth">Geçerli bir e-posta adresi girin (ornek@firma.com).</p>
          )}
        </div>
      ) : (
        <button type="button" disabled={disabled} onClick={() => setEditing(true)}
          className={`group/row flex items-center gap-1.5 min-w-0 text-[13px] font-SemiBold text-gray-900 ${disabled ? "cursor-default" : "hover:text-blue-700"} transition-colors`}>
          <span className="truncate">{value || <span className="text-gray-400 italic font-Medium">{placeholder}</span>}</span>
          {!disabled && <FaPencilAlt className="w-2.5 h-2.5 text-gray-300 group-hover/row:text-blue-500 shrink-0" />}
        </button>
      )}
    </InfoCard.Row>
  );
};

const TextRow = ({ label, value }) => (
  <InfoCard.Row label={label}>
    <span className="text-[13px] font-SemiBold text-gray-900 truncate">{value || <span className="text-gray-300">—</span>}</span>
  </InfoCard.Row>
);

// Aktiflik — Rol kartının sağ üstünde (kart başlığı aksiyonu).
const StatusToggle = ({ value, onToggle, isDirty, disabled }) => (
  <button type="button" disabled={disabled} onClick={() => onToggle(!value)}
    className={`flex items-center gap-2 rounded-full pl-2.5 pr-1 py-0.5 border transition-colors ${
      isDirty ? "border-blue-300 bg-blue-50" : "border-gray-200 bg-white"
    } ${disabled ? "cursor-default" : "hover:border-gray-300"}`}
    title={disabled ? undefined : value ? "Pasif yap" : "Aktif yap"}>
    <span className={`text-xs font-SemiBold ${value ? "text-emerald-700" : "text-gray-500"}`}>{value ? "Aktif" : "Pasif"}</span>
    {value ? <FaToggleOn className="w-7 h-7 text-emerald-500" /> : <FaToggleOff className="w-7 h-7 text-gray-300" />}
  </button>
);

// ─── Build form state from user ───
const buildFormState = (user) => ({
  name: user?.name || "",
  lastName: user?.lastName || "",
  email: user?.email || "",
  tel: user?.tel || "",
  subRole: user?.subRole || "",
  roleTemplate: user?.roleTemplate ? String(user.roleTemplate) : "",
  // Firma — yalnız müşteri kullanıcısında değiştirilebilir (#232)
  firmId: user?.firm?._id ? String(user.firm._id) : "",
  firmName: user?.firm?.firmName || "",
  cardId: user?.cardId || "",
  // Fabrika NFC kartı (D-032): kullanıcı formunda (oluşturma/drawer) vardı ama
  // bu detay sayfasında yoktu — kart kaybında paneli kullanamıyorduk.
  nfcCardId: user?.nfcCardId || "",
  isActive: user?.isActive !== false,
  autoReportMailConfig: user?.autoReportMailConfig || {
    daily: { enabled: false, sendTime: "08:00" },
    weekly: { enabled: false, sendTime: "08:00", daysOfWeek: [] },
    monthly: { enabled: false, sendTime: "08:00", dayOfMonth: 1 },
  },
});

// ─── Main Component ───
export default function UserDetailRoutePage() {
  const { userId } = useParams();
  const navigate = useNavigate();
  const location = useLocation();
  const { auth } = useAuth();
  const { hasPermission } = usePermission();

  const [activeTab, setActiveTab] = useState("info");
  const [user, setUser] = useState(location.state?.user || null);
  const [form, setForm] = useState(() => buildFormState(location.state?.user));
  const [alerts, setAlerts] = useState([]);
  const [isDeviceAssignmentOpen, setIsDeviceAssignmentOpen] = useState(false);
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
  const [saving, setSaving] = useState(false);

  const [getUser, { data: userData, isLoading, isFetching }] = useLazyGetUserQuery();
  const [fetchRoleTemplates, { data: roleTemplatesData }] = useLazyGetRoleTemplatesQuery();
  const [searchFirms, { data: firmSearchData, isFetching: isFirmSearching }] =
    useLazySearchFirmsForDropdownQuery();
  const [updateUser] = useUpdateUserMutation();
  const [deleteUser, { isLoading: isDeleting }] = useDeleteUserByIDMutation();
  const { data: devicesData } = useGetTrustedDevicesQuery(undefined, { skip: !auth?.accessToken });
  const twoFactorEnabled = devicesData?.data?.twoFactorEnabled === true;
  const canEdit = hasPermission("users.edit");
  // Firma arama ucu firms.read ister
  const canChangeFirm = hasPermission("users.changeFirm") && hasPermission("firms.read");
  const canChangeStatus = hasPermission("users.changeStatus");
  const canDelete = hasPermission("users.delete");
  // Cihaz Ata izne bağlı (#382; önce yalnız admin) — sunucu kapısıyla aynı
  const canAssignDevices = hasPermission("users.assignDevices");
  const canViewPermissions = hasPermission("users.viewPermissions");
  const canEditPermissions = hasPermission("users.editPermissions");
  // Son Hareketler sekmesi (#236)
  const canViewActivity = hasPermission("users.activity");
  const canSeePermissionsTab = canViewPermissions || canEditPermissions;
  // Cüzdan sekmesi — yalnız son kullanıcıda (#397)
  const canViewWallet = user?.role === "enduser" && hasPermission("users.walletRead");
  const detailTabs = useMemo(
    () => [
      { id: "info", label: "Bilgiler", icon: FaUser, bg: "bg-indigo-600" },
      ...(canViewWallet ? [{ id: "wallet", label: "Cüzdan", icon: FaWallet, bg: "bg-indigo-600" }] : []),
      ...(canSeePermissionsTab ? [{ id: "permissions", label: "Yetkiler", icon: FaShieldAlt, bg: "bg-indigo-600" }] : []),
      ...(canViewActivity ? [{ id: "activity", label: "Son Hareketler", icon: FaHistory, bg: "bg-indigo-600" }] : []),
    ],
    [canViewWallet, canSeePermissionsTab, canViewActivity]
  );
  const token = auth?.accessToken;

  const fetchUser = useCallback(() => {
    if (token && userId) getUser({ token, id: userId });
  }, [token, userId, getUser]);

  useEffect(() => { fetchUser(); }, [fetchUser]);
  useEffect(() => {
    if (userData?.data?.user) {
      setUser(userData.data.user);
      setForm(buildFormState(userData.data.user));
    }
  }, [userData]);

  // Alt rol = firma rol şablonu — şablon listesini kullanıcının firmasından çek.
  // Firma formda değiştirildiyse yeni firmanın şablonları gelir (#232).
  const templateRoles = ["storage", "dealer", "customer"];
  const userFirmId = form.firmId || user?.firm?._id || user?.firm;
  useEffect(() => {
    if (token && userFirmId && templateRoles.includes(user?.role)) {
      fetchRoleTemplates({ token, id: userFirmId });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [token, userFirmId, user?.role, fetchRoleTemplates]);
  const roleTemplates = roleTemplatesData?.data?.templates || [];

  const originalForm = useMemo(() => buildFormState(user), [user]);
  const hasChanges = useMemo(() => {
    if (!user) return false;
    return JSON.stringify(form) !== JSON.stringify(originalForm);
  }, [form, originalForm, user]);

  const isDirty = useCallback((field) => {
    if (!user) return false;
    if (field === "autoReportMailConfig") return JSON.stringify(form.autoReportMailConfig) !== JSON.stringify(originalForm.autoReportMailConfig);
    return form[field] !== originalForm[field];
  }, [form, originalForm, user]);

  const updateForm = (field, value) => setForm((prev) => ({ ...prev, [field]: value }));

  const addAlert = (message, severity, autoRemoveMs = 3000) => {
    const id = Date.now();
    setAlerts((prev) => [...prev, { id, message, severity }]);
    setTimeout(() => { setAlerts((prev) => prev.filter((a) => a.id !== id)); }, autoRemoveMs);
  };

  const handleGoBack = () => {
    const fromPage = location.state?.fromPage;
    const firmId = location.state?.firmId;
    if (fromPage === "Firma" && firmId) {
      navigate(`/Anasayfa/Firma/${firmId}`, { state: { toPageTab: "users" } });
    } else if (fromPage) {
      navigate(-1);
    } else {
      navigate("/Anasayfa/Kullanıcı");
    }
  };

  // Ortak PasswordConfirmModal onConfirm ile { password, passwordConfirm, code? } gönderir.
  const handleDelete = async ({ password, passwordConfirm, code }) => {
    if (!token || !userId) return;
    try {
      await deleteUser({
        input: {
          id: userId,
          password,
          passwordConfirm,
          ...(code ? { code } : {}),
        },
        token,
      }).unwrap();
      addAlert("Kullanıcı başarıyla silindi", "success");
      setShowDeleteConfirm(false);
      setTimeout(() => navigate("/Anasayfa/Kullanıcı"), 1000);
    } catch (err) {
      addAlert(err?.data?.message || "Silme işlemi başarısız", "error");
    }
  };

  const handleReset = () => setForm(buildFormState(user));

  // Müşteri firması seçici (#232) — rol/firma tipi uyumu server'da da denetlenir
  const handleFirmSearch = useCallback(
    (search) => {
      if (token) searchFirms({ token, search, firmType: "customer" });
    },
    [token, searchFirms]
  );
  const firmOptions = useMemo(
    () => (firmSearchData?.data?.firms || []).map((f) => ({ id: f._id, label: f.firmName })),
    [firmSearchData]
  );
  // Şablonlar firmaya ait: firma değişince eski şablon geçersiz, yenisi seçilmeli
  const handleFirmChange = (opt) => {
    if (!opt?.id || opt.id === form.firmId) return;
    const backToOriginal = opt.id === originalForm.firmId;
    setForm((prev) => ({
      ...prev,
      firmId: opt.id,
      firmName: opt.label,
      roleTemplate: backToOriginal ? originalForm.roleTemplate : "",
      subRole: backToOriginal ? originalForm.subRole : "",
    }));
  };

  const handleSave = async () => {
    if (!user || !token || !hasChanges) return;

    const firmDegisti = form.firmId !== originalForm.firmId;
    if (firmDegisti && !form.roleTemplate) {
      addAlert("Yeni firmadan bir rol şablonu seçin.", "error");
      return;
    }

    // E-posta giris kimligi oldugu icin sessizce degistirilmez: kullanici
    // bir daha eski adresiyle giremez ve acik oturumlari duser.
    const yeniEmail = (form.email || "").trim().toLowerCase();
    const eskiEmail = (user.email || "").trim().toLowerCase();
    if (yeniEmail !== eskiEmail) {
      if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(yeniEmail)) {
        addAlert("Geçerli bir e-posta adresi girin.", "error");
        return;
      }
      const onay = window.confirm(
        `E-posta "${eskiEmail}" yerine "${yeniEmail}" olarak değiştirilecek.\n\n` +
          "Bu kullanıcı bundan sonra YENİ adresiyle giriş yapar ve açık " +
          "oturumları kapanır. Devam edilsin mi?"
      );
      if (!onay) return;
    }

    setSaving(true);
    try {
      await updateUser({
        input: {
          id: user._id,
          name: form.name,
          lastName: form.lastName,
          email: form.email,
          tel: form.tel,
          role: user.role,
          subRole: form.subRole,
          // Şablon değiştiyse server subRole + izinleri şablondan uygular
          ...(form.roleTemplate ? { roleTemplate: form.roleTemplate } : {}),
          ...(firmDegisti ? { firmName: form.firmName } : {}),
          cardId: form.cardId,
          nfcCardId: form.nfcCardId,
          isActive: form.isActive,
          autoReportMailConfig: form.autoReportMailConfig,
        },
        token,
      }).unwrap();
      addAlert("Kullanıcı başarıyla güncellendi", "success");
      fetchUser();
    } catch (err) {
      addAlert(err?.data?.message || "Güncelleme hatası", "error");
    }
    setSaving(false);
  };

  // Loading
  if (!user && isLoading) {
    return <DetailSkeleton tabCount={0} sectionRows={[6, 4]} />;
  }

  // Not found
  if (!user && !isLoading) {
    return (
      <DetailNotFound
        icon={FaUser}
        title="Kullanıcı Bulunamadı"
        message="İstenen kullanıcı bilgilerine ulaşılamadı."
        backLabel="Kullanıcılara Dön"
        onGoBack={() => navigate("/Anasayfa/Kullanıcı")}
      />
    );
  }

  const effectiveRole = getEffectiveRole(user);
  const RoleIcon = getRoleIcon(effectiveRole);
  const reportConfig = form.autoReportMailConfig || {};

  const updateReportConfig = (key, updates) => {
    setForm((prev) => ({
      ...prev,
      autoReportMailConfig: {
        ...prev.autoReportMailConfig,
        [key]: { ...prev.autoReportMailConfig[key], ...updates },
      },
    }));
  };

  return (
    <DetailShell
      alerts={alerts}
      onDismissAlert={(id) => setAlerts((prev) => prev.filter((a) => a.id !== id))}
    >
      {/* Silme onayı — ortak şifre + 2FA modalı (kullanıcı listesi ile aynı yapı) */}
      <PasswordConfirmModal
        open={showDeleteConfirm}
        onClose={() => setShowDeleteConfirm(false)}
        onConfirm={handleDelete}
        title="Kullanıcıyı Sil"
        subtitle={`${user.name} ${user.lastName} – bu işlem geri alınamaz.`}
        confirmButtonText="Sil"
        isLoading={isDeleting}
        twoFactorEnabled={twoFactorEnabled}
      />

      {/* Header */}
      <DetailHeader onGoBack={handleGoBack} backLabel="Kullanıcılara Dön">
        <div className="flex items-center gap-2.5 sm:gap-3 flex-1 min-w-0">
          <div className="flex items-center justify-center w-9 h-9 sm:w-10 sm:h-10 bg-gradient-to-r from-indigo-500 to-blue-600 rounded-lg sm:rounded-xl text-white font-bold text-sm shadow-sm shrink-0">
            {user.name?.charAt(0)}{user.lastName?.charAt(0)}
          </div>
          <div className="flex-1 min-w-0">
            <h1 className="text-sm sm:text-base font-bold text-gray-800 truncate">{user.name} {user.lastName}</h1>
            <div className="flex items-center gap-1.5 mt-0.5 text-[11px] sm:text-xs text-gray-500">
              <span className="truncate">{user.email}</span>
              {user.firm?.firmName && (<><span className="text-gray-300">•</span><span className="truncate hidden sm:inline">{user.firm.firmName}</span></>)}
            </div>
          </div>
          <div className="shrink-0 flex items-center gap-1.5 sm:gap-2">
            {isFetching && <div className="w-4 h-4 border-2 border-blue-500 border-t-transparent rounded-full animate-spin" />}
            {/* Rozet rengi subRole'den (şablon adı) türetilir — özel adlı
                şablonlar da ayrışsın; bkz. utils/roleBadge.js */}
            <div className={`flex items-center gap-1.5 px-2.5 sm:px-3 h-9 sm:h-10 rounded-lg sm:rounded-xl border ${getRoleBadgeClass(user?.role, effectiveRole)}`}>
              <RoleIcon className="w-3.5 h-3.5 shrink-0" />
              <div className="flex flex-col leading-tight">
                <span className="text-xs sm:text-sm font-semibold">{getRoleLabel(user?.role, effectiveRole)}</span>
                {user?.subRole && user.role !== user.subRole && (
                  <span className="text-[10px] sm:text-[11px] font-medium opacity-70">{getRoleName(user.role)}</span>
                )}
              </div>
            </div>
            {/* Sunucu kapısıyla aynı: users.assignDevices (#382; #323'te yalnız admin'di) */}
            {effectiveRole === "machineworker" && canAssignDevices && (
              <button onClick={() => setIsDeviceAssignmentOpen(true)} className="flex items-center justify-center gap-1.5 px-2.5 sm:px-3 h-9 sm:h-10 rounded-lg sm:rounded-xl bg-amber-50 border border-amber-200 text-amber-700 hover:bg-amber-100 transition-colors" title="Cihaz Ata">
                <FaDesktop className="w-3.5 h-3.5 shrink-0" /><span className="text-xs font-semibold hidden sm:inline">Cihaz Ata</span>
              </button>
            )}
            {canDelete && (
            <button onClick={() => setShowDeleteConfirm(true)} className="flex items-center justify-center gap-1.5 px-2.5 sm:px-3.5 h-9 sm:h-10 rounded-lg sm:rounded-xl bg-red-600 text-white shadow-sm hover:bg-red-700 hover:shadow-md active:bg-red-800 transition-all" title="Sil">
              <FaTrash className="w-3.5 h-3.5 shrink-0" /><span className="text-xs sm:text-sm font-semibold hidden sm:inline">Sil</span>
            </button>
            )}
          </div>
        </div>
      </DetailHeader>

      {/* Sekmeler — firma/cihaz detayı ile aynı ortak DetailTabs.
          Yetkiler ve Son Hareketler kendi izinleriyle; yalnız Bilgiler varsa çubuk yok. */}
      {detailTabs.length > 1 && (
        <DetailTabs
          tabs={detailTabs}
          activeTab={activeTab}
          onChange={setActiveTab}
        />
      )}

      {/* Content */}
      <div className="flex-1 min-h-0 overflow-y-auto bg-white sm:bg-gray-50/40">
        {activeTab === "wallet" && canViewWallet ? (
          <div className="p-3 sm:p-4 md:p-6">
            <EndUserWalletTab
              userId={userId}
              userName={[user?.name, user?.lastName].filter(Boolean).join(" ")}
            />
          </div>
        ) : activeTab === "activity" && canViewActivity ? (
          <div className="p-3 sm:p-4 md:p-6">
            <UserActivityTab userId={userId} />
          </div>
        ) : activeTab === "permissions" && canSeePermissionsTab ? (
          <div className="p-3 sm:p-4 md:p-6">
            {/* İzinler tek kaynaktan (firma Kullanıcı Şablonları) yönetilir — burada salt görüntüleme */}
            <PermissionsTab user={user} readOnly />
          </div>
        ) : (
        <div className="space-y-4 sm:space-y-5 p-3 sm:p-4 md:p-6">

          {/* Bilgi kartları — Firma Bilgileri deseni (#168); aynı satırdakiler eşit yükseklikte */}
          <div className="grid gap-3 sm:gap-4 sm:grid-cols-2 xl:grid-cols-4">
            <InfoCard icon={FaUser} iconBg="bg-indigo-500" title="Kişisel Bilgiler"
              dirty={isDirty("name") || isDirty("lastName")}>
              <EditableRow label="İsim" value={form.name} isDirty={isDirty("name")}
                onChange={(val) => updateForm("name", val)} placeholder="İsim" disabled={!canEdit} />
              <EditableRow label="Soyisim" value={form.lastName} isDirty={isDirty("lastName")}
                onChange={(val) => updateForm("lastName", val)} placeholder="Soyisim" disabled={!canEdit} />
              <TextRow label="Kayıt Tarihi" value={user.createdInfo ? FormatDateString(user.createdInfo) : null} />
            </InfoCard>

            <InfoCard icon={FaEnvelope} iconBg="bg-emerald-500" title="İletişim"
              dirty={isDirty("email") || isDirty("tel")}>
              {/* E-posta AYNI ZAMANDA GIRIS KIMLIGI: degistigi anda kullanici
                  eski adresiyle giremez ve acik oturumlari duser (server
                  tokenVersion'i artirir). Bu yuzden kaydetmeden once ayrica
                  onaylatiliyor. */}
              <EditableRow label="E-posta" value={form.email} type="email" isDirty={isDirty("email")}
                onChange={(val) => updateForm("email", val)} placeholder="E-posta" disabled={!canEdit} />
              <EditableRow label="Telefon" value={form.tel} type="tel" isDirty={isDirty("tel")}
                onChange={(val) => updateForm("tel", val)} placeholder="Telefon" disabled={!canEdit} />
            </InfoCard>

            {/* Rol — aktiflik anahtarı kartın sağ üstünde */}
            <InfoCard icon={FaShieldAlt} iconBg="bg-violet-500" title="Rol"
              dirty={isDirty("roleTemplate") || isDirty("subRole")}
              action={
                <StatusToggle value={form.isActive} isDirty={isDirty("isActive")} disabled={!canChangeStatus}
                  onToggle={(val) => updateForm("isActive", val)} />
              }>
              {/* ROL = subRole; şablonlu rollerde rol doğrudan buradan şablon seçilerek
                  değiştirilir (server subRole + izinleri şablondan uygular) */}
              {templateRoles.includes(user.role) ? (
                <InfoCard.Row label="Rol (Şablon)" dirty={isDirty("roleTemplate")}>
                  {canEdit ? (
                    <RoleTemplateDropdown templates={roleTemplates} value={form.roleTemplate}
                      displayValue={getSubRoleName(form.subRole)}
                      onChange={(id, t) => {
                        setForm((prev) => ({
                          ...prev,
                          roleTemplate: id,
                          subRole: t ? t.name : prev.subRole,
                        }));
                      }} />
                  ) : (
                    <span className="text-[13px] font-SemiBold text-gray-900 truncate">{getSubRoleName(form.subRole)}</span>
                  )}
                </InfoCard.Row>
              ) : (
                <InfoCard.Row label="Rol">
                  <span className={`inline-flex items-center px-2 py-0.5 rounded-md text-xs font-SemiBold border ${getRoleColor(effectiveRole)}`}>
                    {getRoleName(effectiveRole)}
                  </span>
                </InfoCard.Row>
              )}
              {effectiveRole === "service" && (
                <EditableRow label="Kart ID" value={form.cardId} isDirty={isDirty("cardId")}
                  onChange={(val) => updateForm("cardId", val)} placeholder="Kart ID" disabled={!canEdit} />
              )}
              {/* Fabrika NFC kartı — tüm depo kullanıcılarında (D-032).
                  updateUser ucu nfcCardId'yi zaten kabul ediyordu; eksik olan
                  yalnızca arayüzdü. Boş bırakılırsa kart kaldırılır. */}
              {user.role === "storage" && (
                <EditableRow label="Fabrika NFC Kart" value={form.nfcCardId} isDirty={isDirty("nfcCardId")}
                  onChange={(val) => updateForm("nfcCardId", val)} placeholder="Okutun veya girin" disabled={!canEdit} />
              )}
            </InfoCard>

            {/* Firma salt-okunur; yalnız müşteri kullanıcısında users.changeFirm ile
                başka müşteri firmasına taşınabilir (#232). Bayser No + firmaya git
                (cihaz detayı deseni) */}
            {(() => {
              const firm = user.firm;
              const firmInfoPermission =
                firm?.firmType === "customer"
                  ? "customers.info"
                  : firm?.firmType === "dealer"
                  ? "dealers.info"
                  : "firms.info";
              const canGoToFirm = !!firm?._id && hasPermission(firmInfoPermission);
              // Firma tipi renkleri tek kaynaktan (#215)
              const { label: firmTypeLabel, chip: firmTypeBadgeColor } = getFirmTypeStyle(firm);
              const activeBadgeColor = firm?.isActive
                ? "bg-emerald-100 text-emerald-800"
                : "bg-red-100 text-red-700";
              return (
                <InfoCard icon={FaBuilding} iconBg="bg-blue-500" title="Firma Bilgileri"
                  dirty={isDirty("firmId")}
                  linkTo={canGoToFirm ? `/Anasayfa/Firma/${firm._id}` : undefined}
                  linkState={{ fromPage: "Kullanıcı" }} linkLabel="Firma bilgilerine git">
                  {user.role === "customer" && canChangeFirm ? (
                    <InfoCard.Row label="Firma Adı" dirty={isDirty("firmId")}>
                      <CustomDropdown
                        compact
                        className="w-44 sm:w-52"
                        value={form.firmId ? { id: form.firmId, label: form.firmName } : null}
                        options={firmOptions}
                        onChange={handleFirmChange}
                        onSearch={handleFirmSearch}
                        loading={isFirmSearching}
                        showSearch
                        placeholder="Firma ara ve seçin"
                        searchPlaceholder="Firma adı yazın..."
                      />
                    </InfoCard.Row>
                  ) : (
                    <TextRow label="Firma Adı" value={firm?.firmName} />
                  )}
                  {isDirty("firmId") && (
                    <p className="text-[11px] text-blue-600 px-1">
                      Kaydetmeden önce Rol kartından yeni firmanın şablonunu seçin.
                    </p>
                  )}
                  <TextRow label="Bayser No" value={firm?.bayserNo ?? null} />
                  <InfoCard.Row label="Firma Durumu">
                    <div className="flex items-center gap-2 shrink-0">
                      <span className={`text-xs font-Bold px-2.5 py-0.5 rounded-full ${firmTypeBadgeColor}`}>{firmTypeLabel}</span>
                      <span className={`text-xs font-Bold px-2.5 py-0.5 rounded-full ${activeBadgeColor}`}>{firm?.isActive ? "Aktif" : "Pasif"}</span>
                    </div>
                  </InfoCard.Row>
                </InfoCard>
              );
            })()}
          </div>

          {/* Otomatik Rapor Mail Ayarları */}
          {effectiveRole === "customer" && (
            <section className="bg-white rounded-xl sm:rounded-2xl shadow-sm border border-gray-200 p-3 sm:p-5">
              <div className="flex items-center gap-2 mb-3">
                <h3 className="text-[13px] font-bold text-gray-700 uppercase tracking-wide">Otomatik Rapor Mail Ayarları</h3>
                {isDirty("autoReportMailConfig") && <span className="w-2 h-2 rounded-full bg-blue-500" />}
              </div>
              <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
                {[
                  { key: "daily", label: "Günlük Rapor" },
                  { key: "weekly", label: "Haftalık Rapor" },
                  { key: "monthly", label: "Aylık Rapor" },
                ].map(({ key, label }) => {
                  const cfg = reportConfig[key] || {};
                  return (
                    <div key={key} className={`rounded-xl p-3 sm:p-4 border transition-colors ${cfg.enabled ? "bg-green-50 border-green-200" : "bg-slate-50 border-gray-200"}`}>
                      <div className="flex items-center justify-between mb-2">
                        <span className="text-xs font-semibold text-gray-700">{label}</span>
                        <button onClick={() => updateReportConfig(key, { enabled: !cfg.enabled })} className="p-0.5">
                          {cfg.enabled
                            ? <FaToggleOn className="w-6 h-6 text-green-500 hover:text-green-600 transition-colors" />
                            : <FaToggleOff className="w-6 h-6 text-gray-300 hover:text-gray-400 transition-colors" />}
                        </button>
                      </div>
                      {cfg.enabled && (
                        <div className="space-y-2">
                          <div>
                            <span className="text-[11px] text-gray-500">Gönderim Saati</span>
                            <input type="time" value={cfg.sendTime || "08:00"}
                              onChange={(e) => updateReportConfig(key, { sendTime: e.target.value || "08:00" })}
                              className="w-full border border-gray-300 rounded-lg px-2.5 py-1 text-sm focus:ring-2 focus:ring-green-400 focus:outline-none mt-0.5" />
                          </div>
                          {key === "weekly" && (
                            <div>
                              <span className="text-[11px] text-gray-500">Günler</span>
                              <div className="flex flex-wrap gap-1 mt-1">
                                {WEEK_DAYS.map((day) => {
                                  const isChecked = cfg.daysOfWeek?.includes(day.id);
                                  return (
                                    <button key={day.id} type="button"
                                      onClick={() => {
                                        const days = cfg.daysOfWeek || [];
                                        const updated = isChecked ? days.filter((d) => d !== day.id) : [...days, day.id];
                                        updateReportConfig("weekly", { daysOfWeek: updated });
                                      }}
                                      className={`px-2 py-0.5 text-[10px] font-medium rounded-md border transition-colors ${isChecked ? "bg-green-500 text-white border-green-500" : "bg-white text-gray-600 border-gray-300 hover:border-green-400"}`}>
                                      {day.label}
                                    </button>
                                  );
                                })}
                              </div>
                            </div>
                          )}
                          {key === "monthly" && (
                            <div>
                              <span className="text-[11px] text-gray-500">Ayın Günü</span>
                              <input type="number" min={1} max={31} value={cfg.dayOfMonth || 1}
                                onChange={(e) => {
                                  const val = Math.min(Math.max(Number(e.target.value) || 1, 1), 31);
                                  updateReportConfig("monthly", { dayOfMonth: val });
                                }}
                                className="w-full border border-gray-300 rounded-lg px-2.5 py-1 text-sm focus:ring-2 focus:ring-green-400 focus:outline-none mt-0.5" />
                            </div>
                          )}
                        </div>
                      )}
                    </div>
                  );
                })}
              </div>
            </section>
          )}

        </div>
        )}
      </div>

      {/* Sticky Save Bar */}
      {hasChanges && (canEdit || canChangeStatus) && (
        <div className="shrink-0 border-t border-gray-200 bg-white px-3 py-2.5 sm:px-5 sm:py-3">
          <div className="flex items-center justify-between">
            <p className="text-xs sm:text-sm text-gray-500">
              <span className="inline-flex items-center gap-1"><span className="w-2 h-2 rounded-full bg-blue-500" /> Kaydedilmemiş değişiklikler var</span>
            </p>
            <div className="flex items-center gap-2">
              <button onClick={handleReset} disabled={saving}
                className="flex items-center gap-1.5 px-3 py-1.5 sm:px-4 sm:py-2 text-xs sm:text-sm font-medium text-gray-700 bg-gray-100 rounded-xl hover:bg-gray-200 transition-colors disabled:opacity-50">
                <FaUndo className="w-3 h-3" /> <span>Geri Al</span>
              </button>
              <button onClick={handleSave} disabled={saving}
                className="flex items-center gap-1.5 px-4 py-1.5 sm:px-5 sm:py-2 text-xs sm:text-sm font-semibold text-white bg-gradient-to-r from-indigo-600 to-blue-600 rounded-xl hover:from-indigo-700 hover:to-blue-700 shadow-sm hover:shadow-md transition-all disabled:opacity-50">
                {saving ? <div className="w-3.5 h-3.5 border-2 border-white border-t-transparent rounded-full animate-spin" /> : <FaSave className="w-3.5 h-3.5" />}
                <span>{saving ? "Kaydediliyor..." : "Kaydet"}</span>
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Device Assignment Modal */}
      {effectiveRole === "machineworker" && (
        <DeviceAssignmentModal
          open={isDeviceAssignmentOpen}
          onClose={() => setIsDeviceAssignmentOpen(false)}
          token={token}
          machineWorkerId={userId}
          selectedUser={user}
          onSuccess={() => { setIsDeviceAssignmentOpen(false); fetchUser(); addAlert("Cihaz ataması güncellendi", "success"); }}
        />
      )}
    </DetailShell>
  );
}
